import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import HeroSection from './HeroSection';
import ManifestoSection from './ManifestoSection';
import DoodVariants from './DoodVariants';
import FloatingNotes from './FloatingNotes';
import ScribbleCanvas from './ScribbleCanvas';

// Random scribble doodles that appear on page load
function RandomScribbles() {
  const scribbles = useMemo(() => {
    const items = [];
    const count = 8 + Math.floor(Math.random() * 6);
    for (let i = 0; i < count; i++) {
      const type = Math.floor(Math.random() * 4);
      items.push({
        id: i,
        top: 5 + Math.random() * 90,
        left: Math.random() * 95,
        rotation: (Math.random() - 0.5) * 40,
        opacity: 0.06 + Math.random() * 0.08,
        scale: 0.5 + Math.random() * 1,
        type,
      });
    }
    return items;
  }, []);

  return (
    <div className="random-scribbles">
      {scribbles.map(s => (
        <svg
          key={s.id}
          className="bg-scribble"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            transform: `rotate(${s.rotation}deg) scale(${s.scale})`,
            opacity: s.opacity,
          }}
          viewBox="0 0 60 60"
          width="50"
          height="50"
        >
          {s.type === 0 && (
            <circle cx="30" cy="30" r="20" fill="none" stroke="#1a1a1a" strokeWidth="2" />
          )}
          {s.type === 1 && (
            <path d="M 5,30 Q 15,5 30,30 Q 45,55 55,30" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
          )}
          {s.type === 2 && (
            <>
              <line x1="10" y1="10" x2="50" y2="50" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
              <line x1="50" y1="10" x2="10" y2="50" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
            </>
          )}
          {s.type === 3 && (
            <path d="M 10,50 L 30,10 L 50,50 Z" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      ))}
    </div>
  );
}

// Footer
function Footer() {
  return (
    <footer className="footer-section">
      <svg viewBox="0 0 500 20" width="100%" height="20" className="footer-divider">
        <path
          d="M 0,10 Q 62,2 125,12 Q 187,20 250,8 Q 312,0 375,14 Q 437,22 500,10"
          fill="none"
          stroke="#1a1a1a"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.2"
        />
      </svg>
      <div className="footer-content">
        <p className="footer-text">
          made at 3am by someone who missed a 100x
        </p>
        <p className="footer-text footer-small">
          this is not financial advice. this is a doodle.
        </p>
        <p className="footer-text footer-small" style={{ marginTop: '8px', opacity: 0.4 }}>
          © 2025 DOOD — all rights probably don't matter
        </p>
      </div>

      {/* Tiny dood in footer */}
      <svg viewBox="0 0 50 50" width="40" height="40" className="footer-dood">
        <path
          d="M 12,42 Q 5,40 6,30 Q 4,22 9,18 Q 11,10 18,9 Q 22,5 28,6 Q 34,5 39,11 Q 45,16 44,25 Q 46,33 41,39 Q 39,44 33,45 Q 25,48 18,45 Z"
          fill="#f5f3ee"
          stroke="#1a1a1a"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="20" cy="28" r="2" fill="#1a1a1a" />
        <circle cx="31" cy="28" r="2" fill="#1a1a1a" />
        <path d="M 22,35 Q 25,38 29,35" fill="none" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </footer>
  );
}

// Ticker tape at top
function TickerTape() {
  const items = [
    'DOOD IS THE WAY',
    '🖍️',
    'BRING BACK RANDOM COINS',
    '✏️',
    'NO ROADMAP NEEDED',
    '🖍️',
    'JUST VIBES',
    '✏️',
    'THIS FEELS RIGHT',
    '🖍️',
    'APE RESPONSIBLY (or not)',
    '✏️',
    'STRONG DOOD FUNDAMENTALS',
    '🖍️',
  ];

  const repeatedItems = [...items, ...items];

  return (
    <div className="ticker-tape">
      <div className="ticker-content">
        {repeatedItems.map((item, i) => (
          <span key={i} className="ticker-item">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// Random hover scribble lines
function HoverScribbles() {
  const [scribbles, setScribbles] = useState([]);

  useEffect(() => {
    let lastTime = 0;
    const handleMove = (e) => {
      const now = Date.now();
      if (now - lastTime < 400) return;
      if (Math.random() > 0.15) return;
      lastTime = now;

      const id = now;
      const size = 15 + Math.random() * 25;
      setScribbles(prev => [
        ...prev.slice(-8),
        {
          id,
          x: e.clientX - size / 2,
          y: e.clientY - size / 2,
          size,
          rotation: Math.random() * 360,
        },
      ]);

      setTimeout(() => {
        setScribbles(prev => prev.filter(s => s.id !== id));
      }, 1500);
    };

    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <div className="hover-scribbles">
      {scribbles.map(s => (
        <svg
          key={s.id}
          className="hover-scribble"
          style={{
            position: 'fixed',
            left: s.x,
            top: s.y,
            width: s.size,
            height: s.size,
            transform: `rotate(${s.rotation}deg)`,
            pointerEvents: 'none',
          }}
          viewBox="0 0 30 30"
        >
          <path
            d="M 5,15 Q 10,5 15,15 Q 20,25 25,15"
            fill="none"
            stroke="#1a1a1a"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.15"
          />
        </svg>
      ))}
    </div>
  );
}

function App() {
  const [isScribbling, setIsScribbling] = useState(false);

  return (
    <div className="App">
      <ScribbleCanvas active={isScribbling} onClose={() => setIsScribbling(false)} />
      <RandomScribbles />
      <TickerTape />
      <HeroSection
        isScribbling={isScribbling}
        onScribbleToggle={() => setIsScribbling(prev => !prev)}
      />
      <ManifestoSection />
      <DoodVariants />
      <Footer />
      <FloatingNotes />
      <HoverScribbles />
    </div>
  );
}

export default App;
